import React, { useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Alert, AlertDescription } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { Shield, AlertTriangle } from "lucide-react";
import { ControlService } from "../../services/controlService";

const ApplyControlToRiskDialog = ({
  control,
  availableRisks,
  onClose,
  onControlApplied,
  getAccessTokenSilently,
}) => {
  const [selectedRiskId, setSelectedRiskId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleApply = async () => {
    if (!selectedRiskId) {
      setError("Please select a risk");
      return;
    }

    try {
      setLoading(true);
      await ControlService.applyControlToRisk(
        control.id,
        selectedRiskId,
        getAccessTokenSilently
      );
      onControlApplied();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply Control to Risk</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Risk Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Risk</label>
            <Select
              value={selectedRiskId}
              onValueChange={setSelectedRiskId}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a risk" />
              </SelectTrigger>
              <SelectContent>
                {availableRisks.map((risk) => (
                  <SelectItem key={risk.id} value={risk.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {risk.name || risk.description}
                      </span>
                      {risk.description && risk.name !== risk.description && (
                        <span className="text-xs text-muted-foreground">
                          {risk.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {availableRisks.length === 0 && (
            <div className="text-center py-4 text-muted-foreground">
              <p>No available risks to apply this control to.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={
                loading || !selectedRiskId || availableRisks.length === 0
              }
              className="flex items-center gap-2"
            >
              {loading && <Spinner size="sm" />}
              Apply Control
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyControlToRiskDialog;
